/* Mango page (/pages/mango): scroll motion for every section below the hero.
   Added 29/09/2026, replacing the script in the process section. The hero's own entrance is in
   sections/header-mango-gelato.liquid.

   Why the old animations looked broken: the three process illustrations took up no space until
   their animation files had downloaded, so the page grew by several hundred pixels after the
   scroll positions were measured, and any re-measure made while the page was scrolled down came
   out wrong by the distance scrolled (see refreshInit below). Either way, everything further down
   played early, mostly before it was on screen. The illustrations' space is now reserved (see the
   process section styles), re-measures are correct, and they happen whenever the page changes
   height.

   What moves:
   - Awards: heading rises in, the four headline medals drop into place and float gently, the
     other medals follow in a ripple.
   - Process: the mangoes are tossed in from either side, each illustration glides into place and
     plays as it comes up the screen (finished by the time it reaches the middle), numbers,
     lines and text reveal step by step.
   - Signature serve: the photo is uncovered from the bottom and drifts inside its frame.
   - Cocktails and shop: cards rise in one after another, photos uncovered or settling into place.
   - Bottle photo banner: a slow zoom out as it comes into view.
   Scroll-linked motion glides after the scroll (SMOOTH seconds) instead of snapping to it.
   Loops only run while on screen. Visitors whose device asks for reduced motion get everything
   at rest, with the illustrations shown finished.
   The illustration player (lottie) and the animation files load only as the visitor nears the
   process section, and never hold up the rest of the page.
   Each reveal plays once and its scroll trigger is then left alone rather than removed (no
   "once: true" or kill()). Removing triggers while the page opens part way down (a reload, or
   coming back to the page) made the animation library fail, leaving the awards, headings and
   illustrations hidden. */
(function (init) {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})(function () {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;
  gsap.registerPlugin(ScrollTrigger);
  window.ddMangoMotion = "2026-09-29b";

  var LOTTIE_SRC = "https://unpkg.com/lottie-web@5.12.0/build/player/lottie.min.js";
  var SMOOTH = 1;
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isMobile = window.matchMedia("(max-width: 767px)").matches;

  /* ---------- helpers ---------- */

  function one(sel, ctx) { return (ctx || document).querySelector(sel); }
  function all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function kids(el) { return el ? Array.prototype.slice.call(el.children) : []; }
  function section(name) { return document.getElementById("shopify-section-" + name); }

  // The theme scrolls smoothly (html { scroll-behavior: smooth }). To re-measure, ScrollTrigger jumps
  // to the top of the page and back, and Chrome was making that jump smooth too, so whenever the
  // page was re-measured while scrolled down, every position came out wrong by the distance scrolled
  // and the animations fired far too early. Smooth scrolling is now switched off (and the browser
  // made to notice) at the start of each re-measure. ScrollTrigger switches it back on afterwards.
  ScrollTrigger.addEventListener("refreshInit", function () {
    var root = document.documentElement;
    root.style.scrollBehavior = "auto";
    getComputedStyle(root).scrollBehavior;
  });

  // Measure the scroll positions again when the page changes height (lazy images, fonts).
  var refreshTimer;
  var lastHeight = 0;
  function queueRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { ScrollTrigger.refresh(); }, 250);
  }
  document.addEventListener("load", function (e) {
    if (e.target && e.target.tagName === "IMG") queueRefresh();
  }, true);
  if ("ResizeObserver" in window) {
    new ResizeObserver(function () {
      var h = document.body.offsetHeight;
      if (Math.abs(h - lastHeight) > 1) { lastHeight = h; queueRefresh(); }
    }).observe(document.body);
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueRefresh);

  // 30/09/2026: on large screens some sections are drawn a little larger with CSS zoom (the wide
  // page layout in assets/animations-gelato.css). Inside a zoomed section, offsetTop and
  // offsetHeight come back in the section's own, smaller units, so the scroll ranges came out too
  // short and too early and the process animations had mostly played before they were on screen.
  // zoomOf() is the total zoom on an element (1 where nothing is zoomed, so smaller screens are
  // unchanged). It is worked out once per re-measure.
  var zoomCache = new WeakMap();
  ScrollTrigger.addEventListener("refreshInit", function () { zoomCache = new WeakMap(); });
  function zoomOf(el) {
    if (!el || el.nodeType !== 1) return 1;
    var z = zoomCache.get(el);
    if (z === undefined) {
      var own = parseFloat(getComputedStyle(el).zoom);
      z = (own > 0 ? own : 1) * zoomOf(el.parentElement);
      zoomCache.set(el, z);
    }
    return z;
  }
  // Top of an element's layout box on the page. Transforms are ignored, so an element's own
  // movement never shifts the scroll range that drives it.
  function pageTop(el) {
    var y = 0;
    for (var node = el; node; node = node.offsetParent) y += node.offsetTop * zoomOf(node);
    return y;
  }
  function boxHeight(el) { return el.offsetHeight * zoomOf(el); }
  // Scroll range from when point `a` of the element (0 = top edge, 1 = bottom edge) is `sa` of the
  // way down the screen until its point `b` is `sb` of the way down (1 = bottom of the screen).
  function span(el, a, sa, b, sb) {
    return {
      start: function () { return pageTop(el) + boxHeight(el) * a - window.innerHeight * sa; },
      end: function () { return pageTop(el) + boxHeight(el) * b - window.innerHeight * sb; }
    };
  }
  function scrubbed(el, a, sa, b, sb) {
    var st = span(el, a, sa, b, sb);
    st.scrub = SMOOTH;
    return st;
  }
  // Run a looping tween only while `el` is on screen.
  function whileVisible(el, tween) {
    var st = span(el, 0, 1, 1, 0);
    st.onToggle = function (self) { if (self.isActive) tween.play(); else tween.pause(); };
    ScrollTrigger.create(st);
    return tween;
  }
  // A gentle float. It moves `y`, while the scroll-linked motion uses xPercent, yPercent,
  // rotation and scale, so the two add up instead of fighting.
  function float(el, vars, host) {
    if (reduceMotion || !el) return;
    whileVisible(host || el, gsap.to(el, Object.assign({
      y: -10, duration: 2.8, ease: "sine.inOut", yoyo: true, repeat: -1, paused: true
    }, vars)));
  }
  // Fade and rise in once, when the trigger reaches `start`.
  function reveal(targets, vars, trigger, start) {
    targets = (targets || []).filter(function (t) { return !!t; });
    if (!targets.length || reduceMotion) return;
    gsap.from(targets, Object.assign({
      opacity: 0, y: 32, duration: 0.9, ease: "power3.out", stagger: 0.1,
      scrollTrigger: { trigger: trigger || targets[0], start: start || "top 88%" }
    }, vars));
  }
  // A card's title, text and button, once they come up the screen (they sit well below its photo).
  function words(els, delay) {
    if (!els.length || reduceMotion) return;
    gsap.from(els, {
      opacity: 0, y: 18, duration: 0.7, ease: "power2.out", stagger: 0.08, delay: delay || 0,
      scrollTrigger: { trigger: els[0], start: "top 95%" }
    });
  }
  // Things laid out in rows (medals): each row drops in as it comes up the screen, left to right.
  function rows(els, from, to, start) {
    if (!els.length || reduceMotion) return;
    gsap.set(els, from);
    ScrollTrigger.batch(els, {
      start: start || "top 90%",
      onEnter: function (batch) { gsap.to(batch, Object.assign({ overwrite: true }, to)); }
    });
  }
  // Uncover from the bottom edge up.
  function uncover(tl, el, at, duration) {
    tl.fromTo(el, { clipPath: "inset(100% 0% 0% 0%)" },
      { clipPath: "inset(0% 0% 0% 0%)", duration: duration || 1.2, ease: "power3.inOut" }, at);
  }

  /* ---------- hero: the bottle drifts up a little as the page scrolls on ---------- */

  var hero = section("header-mango-gelato");
  if (hero && !reduceMotion) {
    var bottleWrap = one(".about-hero_image-bottle-wrapper", hero);
    if (bottleWrap) {
      gsap.to(bottleWrap, {
        yPercent: -7, rotation: 2.5, ease: "none",
        scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: SMOOTH }
      });
    }
  }

  /* ---------- awards ---------- */

  var awards = document.getElementById("dd-awards-mango");
  if (awards && !reduceMotion) {
    reveal([one(".dd-awards__eyebrow", awards)].concat(all(".dd-awards__heading-line", awards)),
      { y: 40, stagger: 0.1 }, awards, "top 78%");

    var medalList = one(".dd-awards__hero", awards);
    var medals = all(".dd-awards__tile-media", awards);
    if (medalList && medals.length) {
      rows(medals, { opacity: 0, y: 80, scale: 0.6, rotation: function (i) { return [-16, 12, -10, 14, -12, 10][i % 6]; } },
        { opacity: 1, y: 0, scale: 1, rotation: 0, duration: 1.1, ease: "back.out(1.5)", stagger: 0.12 });
      rows(all(".dd-awards__tile-headline, .dd-awards__tile-competition", awards), { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", stagger: 0.06, delay: 0.35 });
      medals.forEach(function (m, i) {
        float(one("img", m), { y: -8, duration: 2.6 + i * 0.35, delay: 1.2 + i * 0.3 }, medalList);
      });
    }

    var row = one(".dd-awards__row", awards);
    if (row) {
      reveal([one(".dd-awards__strip-label", awards)], { y: 16, duration: 0.8 }, row, "top 92%");
      rows(all(".dd-awards__medal", row), { opacity: 0, y: 40, scale: 0.8 },
        { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "back.out(1.4)", stagger: 0.07 }, "top 92%");
    }
  }

  /* ---------- process ---------- */

  var process = section("about-process-mango-gelato");
  if (process) {
    reveal([one(".anim_eyebrow", process), one("h1", process)], { y: 40, stagger: 0.12 }, process, "top 80%");

    // Step 1: the two mangoes are tossed in from either side, come to rest as the pair reaches
    // the middle of the screen, then carry on up and away.
    all(".anim_image-wrapper.is-anim-11", process).forEach(function (wrap) {
      if (reduceMotion) return;
      all("img", wrap).forEach(function (img, i) {
        var dir = img.classList.contains("is-anim-11") ? -1 : 1; // the left mango comes from the left
        gsap.timeline({ scrollTrigger: scrubbed(wrap, 0, 1, 1, 0) })
          .fromTo(img, { xPercent: 38 * dir, yPercent: 50, rotation: 42 * dir, scale: 0.8 },
            { xPercent: 0, yPercent: 0, rotation: 0, scale: 1, ease: "power2.out", duration: 1 })
          .to(img, { xPercent: -8 * dir, yPercent: -22, rotation: -12 * dir, ease: "sine.in", duration: 1 });
        float(img, { y: i ? -12 : -9, duration: i ? 3.1 : 2.5, delay: i * 0.4 }, wrap);
      });
    });

    // Steps 2 to 4: each illustration glides into place as it comes up the screen.
    var lotties = all("[data-animation-type=lottie]", process);
    if (!reduceMotion) {
      var glides = [
        { yPercent: 14, scale: 0.92 },               // sun rising over the farm
        { yPercent: 12, rotation: -4, scale: 0.94 },  // the still
        { yPercent: 16, rotation: 3, scale: 0.88 }    // the mango
      ];
      lotties.forEach(function (el, i) {
        gsap.fromTo(el, glides[i] || glides[0],
          { yPercent: 0, rotation: 0, scale: 1, ease: "power2.out", scrollTrigger: scrubbed(el, 0, 1, 0.5, 0.5) });
      });
      if (lotties[2]) float(lotties[2], { y: -10, duration: 3 });
    }

    // ...and plays from the moment it appears at the bottom of the screen until it reaches the
    // middle. Player and files load when the visitor is within two and a half screens of it.
    var lottieLib = null;
    var lottiesStarted = false;
    function loadLottie() {
      if (!lottieLib) {
        lottieLib = new Promise(function (resolve, reject) {
          if (window.lottie) { resolve(window.lottie); return; }
          var s = document.createElement("script");
          s.src = LOTTIE_SRC;
          s.async = true;
          s.onload = function () { if (window.lottie) resolve(window.lottie); else reject(); };
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      return lottieLib;
    }
    function playLottie(lib, el) {
      var anim = lib.loadAnimation({
        container: el, renderer: "svg", loop: false, autoplay: false, path: el.getAttribute("data-src")
      });
      anim.addEventListener("DOMLoaded", function () {
        var last = Math.max(0, anim.totalFrames - 1);
        if (reduceMotion) { anim.goToAndStop(last, true); return; }
        var state = { frame: 0 };
        anim.goToAndStop(0, true);
        gsap.to(state, {
          frame: last, ease: "none",
          scrollTrigger: scrubbed(el, 0, 0.9, 0.5, 0.45),
          onUpdate: function () { anim.goToAndStop(state.frame, true); }
        });
      });
    }
    function startLotties() {
      if (lottiesStarted) return;
      lottiesStarted = true;
      loadLottie().then(function (lib) {
        lotties.forEach(function (el) { playLottie(lib, el); });
      }, function () {});
    }
    if (lotties.length) {
      ScrollTrigger.create({
        start: function () { return pageTop(process) - window.innerHeight * 2.5; },
        end: "max",
        onEnter: function () { startLotties(); }
      });
    }

    // Numbers, lines and text, step by step
    all(".anim_card-content", process).forEach(function (card) {
      if (reduceMotion) return;
      var tl = gsap.timeline({ scrollTrigger: { trigger: card, start: "top 85%" } });
      var number = one(".anim_card-number", card);
      var lines = all(".anim_line-top, .anim_line-bottom", card);
      var text = one(".anim_card-p-wrap", card);
      if (number) tl.from(number, { opacity: 0, yPercent: 70, rotation: -8, duration: 0.8, ease: "power3.out" }, 0);
      if (lines.length) tl.from(lines, { scaleX: 0, transformOrigin: "left center", duration: 1, ease: "power3.inOut", stagger: 0.12 }, 0.05);
      if (text) tl.from(text, { opacity: 0, y: 26, duration: 0.9, ease: "power3.out" }, 0.2);
    });

    // Divider and shop button under the steps
    var processCta = one(".anim_cta-wrap", process);
    if (processCta && !reduceMotion) {
      var ctaTl = gsap.timeline({ scrollTrigger: { trigger: processCta, start: "top 90%" } });
      var divider = one("img", processCta);
      var shopBtn = one(".button", processCta);
      if (divider) ctaTl.from(divider, { scaleX: 0, opacity: 0, duration: 1.1, ease: "power3.inOut" }, 0);
      if (shopBtn) ctaTl.from(shopBtn, { opacity: 0, y: 24, duration: 0.8, ease: "power3.out" }, 0.3);
    }
  }

  /* ---------- signature serve ---------- */

  var temple = section("temple-featured-mango-gelato");
  if (temple && !reduceMotion) {
    var templeGrid = one(".temple_content", temple);
    var photoMask = one(".image-reveal-wrapper", temple);
    var photo = one(".image-reveal-inner", temple);
    if (templeGrid && photoMask && photo) {
      var templeTl = gsap.timeline({ scrollTrigger: { trigger: templeGrid, start: "top 80%" } });
      uncover(templeTl, photoMask, 0, 1.3);
      templeTl.fromTo(photo, { scale: 1.35 }, { scale: 1.1, duration: 1.7, ease: "power3.out" }, 0);
      var frame = one(".cocktail_image-frame", temple);
      var badge = one(".temple_badge-split", temple);
      if (frame) templeTl.from(frame, { opacity: 0, scale: 0.96, duration: 1.2, ease: "power2.out" }, 0.3);
      if (badge) templeTl.from(badge, { opacity: 0, scale: 0.4, rotation: -90, duration: 1, ease: "back.out(1.7)" }, 0.8);
      // the photo drifts inside its frame while the section passes
      gsap.fromTo(photo, { yPercent: -4 }, { yPercent: 4, ease: "none", scrollTrigger: scrubbed(templeGrid, 0, 1, 1, 0) });
    }
    var templeCopy = one(".temple-split-image_content-right", temple);
    if (templeCopy) reveal(kids(templeCopy), { y: 30, stagger: 0.1 }, templeCopy, "top 85%");
  }

  /* ---------- cocktails ---------- */

  var cocktails = section("related-cocktails-mango-gelato");
  if (cocktails && !reduceMotion) {
    var cocktailHead = one(".text-align-center", cocktails);
    if (cocktailHead) reveal(all("h2, img", cocktailHead), { y: 30, stagger: 0.12 }, cocktailHead, "top 85%");
    all(".cocktail-related_item", cocktails).forEach(function (card, i) {
      var d = isMobile ? 0 : (i % 3) * 0.15; // side by side on larger screens: one after another
      var tl = gsap.timeline({ scrollTrigger: { trigger: card, start: "top 88%" } });
      tl.from(card, { opacity: 0, y: 70, duration: 1.1, ease: "power3.out" }, d);
      var pic = one(".cocktail-related_image-wrapper", card);
      if (pic) uncover(tl, pic, d + 0.05, 1.2);
      words(kids(card).slice(1), d);
    });
  }

  /* ---------- bottle photo banner ---------- */

  var banner = section("cta-gelato");
  if (banner && !reduceMotion) {
    var bannerBox = one(".section_main", banner);
    var bannerImg = one(".layout_background-image", banner);
    // zooms out as it comes into view, at full sharpness (no zoom) from the middle of the screen on
    if (bannerBox && bannerImg) {
      gsap.fromTo(bannerImg, { scale: 1.12 }, { scale: 1, ease: "none", scrollTrigger: scrubbed(bannerBox, 0, 1, 0.5, 0.5) });
    }
    var bannerCopy = one(".background-cta_wrap", banner);
    if (bannerCopy) reveal(kids(bannerCopy), { y: 40, stagger: 0.15 }, bannerCopy, "top 85%");
  }

  /* ---------- shop ---------- */

  var shop = section("shop-mango-gelato");
  if (shop && !reduceMotion) {
    var shopHead = one(".text-align-center", shop);
    if (shopHead) reveal(all("h2, img", shopHead), { y: 30, stagger: 0.12 }, shopHead, "top 85%");
    all(".cocktail-related_item", shop).forEach(function (item, i) {
      var d = isMobile ? 0 : i * 0.15;
      var tl = gsap.timeline({ scrollTrigger: { trigger: item, start: "top 85%" } });
      // the bottle photo settles into place while it is uncovered (sitewide image reveal)
      var bottleImg = one(".image-reveal-sinner img", item);
      if (bottleImg) {
        bottleImg.style.transition = "none";
        tl.fromTo(bottleImg, { scale: 1.25 }, { scale: 1, duration: 1.6, ease: "power3.out", clearProps: "transform,transition" }, d);
      }
      words(kids(item).slice(1), d);
    });
  }
});